<template>
  <v-container fluid class="pa-4">
    <!-- ✅ HEADER PRINCIPAL -->
    <div class="page-header mb-4">
      <div class="d-flex flex-column">
        <div class="d-flex align-center flex-wrap">
          <div class="d-flex align-center">
            <v-avatar size="48" color="indigo-darken-1" class="mr-3" variant="tonal">
              <v-icon color="white" size="28">mdi-account-cog</v-icon>
            </v-avatar>
            <div>
              <h1 class="text-h4 font-weight-bold text-grey-darken-4 mb-1">Gerenciar Usuários</h1>
              <div class="text-caption text-grey">
                Crie e edite usuários, defina senhas e associe grupos de permissão
              </div>
            </div>
          </div>
          
          <v-spacer></v-spacer>
          
          <div class="d-flex align-center mt-2 mt-sm-0">
            <v-btn
              color="grey-darken-1"
              variant="text"
              @click="voltarMenu"
              class="text-none mr-2"
              prepend-icon="mdi-arrow-left"
            >
              Voltar
            </v-btn>
            
            <v-btn
              color="indigo-darken-1"
              variant="flat"
              @click="carregarDados"
              class="text-none mr-2"
              prepend-icon="mdi-refresh"
              :loading="carregando"
            >
              Atualizar
            </v-btn>

            <v-btn
              v-if="canInsert('gerenciar_usuarios')"
              color="primary"
              variant="flat"
              @click="abrirDialogNovo"
              class="text-none"
              prepend-icon="mdi-plus"
            >
              Novo Usuário
            </v-btn>

            <!-- Mensagem se não tiver permissão -->
            <v-tooltip v-else location="bottom">
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  color="grey"
                  variant="flat"
                  class="text-none"
                  prepend-icon="mdi-lock"
                  disabled
                >
                  Novo Usuário
                </v-btn>
              </template>
              <span>Você não tem permissão de INSERIR usuários</span>
            </v-tooltip>
          </div>
        </div>
      </div>
    </div>

    <!-- Alert de Teste de Permissões -->
    <v-alert type="info" variant="tonal" class="mb-4">
      <v-row align="center">
        <v-col>
          <strong>🔒 Teste de Permissões Ativo:</strong><br>
          <small>
            <strong>Aplicação:</strong> gerenciar_usuarios<br>
            <strong>Permissões verificadas:</strong>
            <v-chip size="x-small" :color="canInsert('gerenciar_usuarios') ? 'success' : 'error'" class="mx-1">
              INSERT: {{ canInsert('gerenciar_usuarios') ? '✓ SIM' : '✗ NÃO' }}
            </v-chip>
            <v-chip size="x-small" :color="canUpdate('gerenciar_usuarios') ? 'success' : 'error'" class="mx-1">
              UPDATE: {{ canUpdate('gerenciar_usuarios') ? '✓ SIM' : '✗ NÃO' }}
            </v-chip>
            <v-chip size="x-small" :color="canDelete('gerenciar_usuarios') ? 'success' : 'error'" class="mx-1">
              DELETE: {{ canDelete('gerenciar_usuarios') ? '✓ SIM' : '✗ NÃO' }}
            </v-chip>
          </small>
        </v-col>
      </v-row>
    </v-alert>

    <v-card class="mb-4">
      <v-card-title class="d-flex align-center pa-4 bg-grey-lighten-4">
        <v-icon left>mdi-table</v-icon>
        <span class="text-h6 ml-2">Lista de Usuários</span>
      </v-card-title>

      <v-card-text>
        <!-- Campo de Busca -->
        <v-row class="mb-4">
          <v-col cols="12" md="6">
            <v-text-field
              v-model="busca"
              prepend-inner-icon="mdi-magnify"
              label="Buscar usuário"
              placeholder="Digite login, nome, email..."
              variant="outlined"
              density="compact"
              clearable
              hide-details
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Tabela de Usuários -->
        <v-data-table
          :headers="headers"
          :items="usuarios"
          :search="busca"
          :loading="carregando"
          class="elevation-1"
          :items-per-page="10"
        >
          <!-- Coluna de Ativo -->
          <template #[`item.active`]="{ item }">
            <v-chip :color="item.active === 'Y' ? 'success' : 'error'" small>
              {{ item.active === 'Y' ? 'Ativo' : 'Inativo' }}
            </v-chip>
          </template>

          <!-- Coluna Admin -->
          <template #[`item.priv_admin`]="{ item }">
            <v-icon v-if="item.priv_admin === 'Y'" color="warning">mdi-shield-crown</v-icon>
            <v-icon v-else color="grey lighten-2">mdi-shield-off</v-icon>
          </template>

          <!-- Coluna de Grupos -->
          <template #[`item.groups`]="{ item }">
            <v-chip-group column>
              <v-chip
                v-for="group in item.groups"
                :key="group.group_id"
                small
                color="blue lighten-4"
              >
                {{ group.description }}
              </v-chip>
            </v-chip-group>
          </template>

          <!-- Ações -->
          <template #[`item.actions`]="{ item }">
            <!-- Botão Editar (apenas com permissão UPDATE) -->
            <v-btn 
              v-if="canUpdate('gerenciar_usuarios')"
              icon 
              size="small" 
              @click="editarUsuario(item)" 
              color="primary"
              title="Editar usuário"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn 
              v-else
              icon 
              size="small" 
              color="grey" 
              disabled
              title="Você não tem permissão para editar"
            >
              <v-icon>mdi-pencil-off</v-icon>
            </v-btn>

            <!-- Botão Excluir (apenas com permissão DELETE e não sendo tabosa) -->
            <v-btn 
              v-if="canDelete('gerenciar_usuarios') && item.login.toLowerCase() !== 'tabosa'"
              icon 
              size="small" 
              @click="confirmarExclusao(item)" 
              color="error"
              title="Excluir usuário"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
            <v-btn 
              v-else
              icon 
              size="small" 
              color="grey" 
              disabled
              :title="item.login.toLowerCase() === 'tabosa' ? 'Usuário protegido - não pode ser excluído' : 'Você não tem permissão para excluir'"
            >
              <v-icon>mdi-delete-off</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Dialog de Criar/Editar Usuário -->
    <v-dialog v-model="dialog" max-width="800px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ modoEdicao ? 'Editar Usuário' : 'Novo Usuário' }}</span>
        </v-card-title>

        <v-card-text>
          <v-form ref="form" v-model="formValido">
            <v-row>
              <!-- Login -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="usuarioAtual.login"
                  label="Login"
                  :rules="[v => !!v || 'Login é obrigatório']"
                  :disabled="modoEdicao"
                  required
                ></v-text-field>
              </v-col>

              <!-- Nome -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="usuarioAtual.name"
                  label="Nome"
                  :rules="[v => !!v || 'Nome é obrigatório']"
                  required
                ></v-text-field>
              </v-col>

              <!-- Email -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="usuarioAtual.email"
                  label="Email (opcional)"
                  type="text"
                  hint="Pode deixar em branco ou usar qualquer texto"
                  persistent-hint
                ></v-text-field>
              </v-col>

              <!-- Telefone -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="usuarioAtual.phone"
                  label="Telefone"
                ></v-text-field>
              </v-col>

              <!-- Senha -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="usuarioAtual.pswd"
                  :label="modoEdicao ? 'Nova Senha (deixe vazio para não alterar)' : 'Senha'"
                  type="password"
                  :rules="modoEdicao ? [] : [v => !!v || 'Senha é obrigatória', v => v.length >= 6 || 'Mínimo 6 caracteres']"
                  :required="!modoEdicao"
                ></v-text-field>
              </v-col>

              <!-- Role -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="usuarioAtual.role"
                  label="Função/Cargo"
                ></v-text-field>
              </v-col>

              <!-- Ativo -->
              <v-col cols="12" md="6">
                <v-switch
                  v-model="usuarioAtual.active"
                  label="Usuário Ativo"
                  true-value="Y"
                  false-value="N"
                  color="success"
                ></v-switch>
              </v-col>

              <!-- Admin -->
              <v-col cols="12" md="6">
                <v-switch
                  v-model="usuarioAtual.priv_admin"
                  label="Administrador"
                  true-value="Y"
                  false-value="N"
                  color="warning"
                >
                  <template v-slot:label>
                    <div class="d-flex align-center">
                      <v-icon left color="warning">mdi-shield-crown</v-icon>
                      <span>Administrador</span>
                    </div>
                  </template>
                </v-switch>
              </v-col>

              <!-- Grupos -->
              <v-col cols="12">
                <v-select
                  v-model="usuarioAtual.grupos"
                  :items="gruposDisponiveis"
                  item-title="description"
                  item-value="group_id"
                  label="Grupos de Permissão"
                  multiple
                  chips
                  closable-chips
                  hint="Um usuário pode pertencer a vários grupos"
                  persistent-hint
                >
                  <template v-slot:chip="{ item, index }">
                    <v-chip v-if="index < 3" size="small" color="blue-lighten-4">
                      {{ item.raw.description }}
                    </v-chip>
                    <span v-if="index === 3" class="text-grey text-caption">
                      (+{{ usuarioAtual.grupos.length - 3 }} outros)
                    </span>
                  </template>
                </v-select>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="fecharDialog">
            Cancelar
          </v-btn>
          <v-btn color="primary" @click="salvarUsuario" :loading="salvando" :disabled="!formValido">
            <v-icon left>mdi-content-save</v-icon>
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de Confirmação de Exclusão -->
    <v-dialog v-model="dialogExclusao" max-width="500px">
      <v-card>
        <v-card-title class="text-h5">Confirmar Exclusão</v-card-title>
        <v-card-text>
          Tem certeza que deseja excluir o usuário <strong>{{ usuarioParaExcluir?.name }}</strong>?
          Esta ação não pode ser desfeita.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="dialogExclusao = false">
            Cancelar
          </v-btn>
          <v-btn color="error" @click="excluirUsuario" :loading="excluindo">
            <v-icon left>mdi-delete</v-icon>
            Excluir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import permissionsService from '@/services/permissionsService';
import { useToast } from 'vue-toastification';
import { usePermissions } from '@/composables/usePermissions';

export default {
  name: 'GerenciarUsuarios',

  setup() {
    // Usar composable de permissões
    const { canInsert, canUpdate, canDelete } = usePermissions();
    
    return {
      canInsert,
      canUpdate,
      canDelete
    };
  },

  data() {
    return {
      usuarios: [],
      busca: '',
      gruposDisponiveis: [],
      dialog: false,
      dialogExclusao: false,
      carregando: false,
      salvando: false,
      excluindo: false,
      formValido: false,
      modoEdicao: false,
      usuarioAtual: this.usuarioVazio(),
      usuarioParaExcluir: null,
      headers: [
        { text: 'Login', value: 'login', sortable: true },
        { text: 'Nome', value: 'name', sortable: true },
        { text: 'Email', value: 'email', sortable: true },
        { text: 'Telefone', value: 'phone', sortable: false },
        { text: 'Ativo', value: 'active', sortable: true, align: 'center' },
        { text: 'Admin', value: 'priv_admin', sortable: true, align: 'center' },
        { text: 'Grupos', value: 'groups', sortable: false },
        { text: 'Ações', value: 'actions', sortable: false, align: 'center' }
      ]
    };
  },

  computed: {
    toast() {
      return useToast();
    }
  },

  mounted() {
    this.carregarDados();
  },

  methods: {
    voltarMenu() {
      this.$router.go(-1); // Volta para a página anterior
    },
    usuarioVazio() {
      return {
        login: '',
        name: '',
        email: '',
        pswd: '',
        active: 'Y',
        priv_admin: 'N',
        role: '',
        phone: '',
        grupos: []
      };
    },

    async carregarDados() {
      this.carregando = true;
      try {
        // Carregar usuários
        const responseUsuarios = await permissionsService.listarUsuarios();
        if (responseUsuarios.status === 'sucesso') {
          this.usuarios = responseUsuarios.usuarios;
        }

        // Carregar grupos
        const responseGrupos = await permissionsService.listarGrupos();
        if (responseGrupos.status === 'sucesso') {
          this.gruposDisponiveis = responseGrupos.grupos;
        }
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
        this.toast.error('Erro ao carregar dados');
      } finally {
        this.carregando = false;
      }
    },

    abrirDialogNovo() {
      this.modoEdicao = false;
      this.usuarioAtual = this.usuarioVazio();
      this.dialog = true;
    },

    editarUsuario(usuario) {
      this.modoEdicao = true;
      this.usuarioAtual = {
        login: usuario.login,
        name: usuario.name,
        email: usuario.email,
        pswd: '', // Não preencher senha na edição
        active: usuario.active || 'Y',
        priv_admin: usuario.priv_admin || 'N',
        role: usuario.role || '',
        phone: usuario.phone || '',
        grupos: usuario.groups ? usuario.groups.map(g => g.group_id) : []
      };
      this.dialog = true;
    },

    async salvarUsuario() {
      if (!this.$refs.form.validate()) return;

      this.salvando = true;
      try {
        let response;
        const dados = { ...this.usuarioAtual };
        
        // Se estiver editando e senha estiver vazia, remover do objeto
        if (this.modoEdicao && !dados.pswd) {
          delete dados.pswd;
        }

        if (this.modoEdicao) {
          response = await permissionsService.atualizarUsuario(this.usuarioAtual.login, dados);
        } else {
          response = await permissionsService.criarUsuario(dados);
        }

        if (response.status === 'sucesso') {
          this.toast.success(response.mensagem);
          this.fecharDialog();
          this.carregarDados();
        } else {
          this.toast.error(response.mensagem || 'Erro ao salvar usuário');
        }
      } catch (error) {
        console.error('Erro ao salvar usuário:', error);
        this.toast.error('Erro ao salvar usuário');
      } finally {
        this.salvando = false;
      }
    },

    confirmarExclusao(usuario) {
      this.usuarioParaExcluir = usuario;
      this.dialogExclusao = true;
    },

    async excluirUsuario() {
      if (!this.usuarioParaExcluir) return;

      this.excluindo = true;
      try {
        const response = await permissionsService.deletarUsuario(this.usuarioParaExcluir.login);
        
        if (response.status === 'sucesso') {
          this.toast.success(response.mensagem);
          this.dialogExclusao = false;
          this.usuarioParaExcluir = null;
          this.carregarDados();
        } else {
          this.toast.error(response.mensagem || 'Erro ao excluir usuário');
        }
      } catch (error) {
        console.error('Erro ao excluir usuário:', error);
        this.toast.error('Erro ao excluir usuário');
      } finally {
        this.excluindo = false;
      }
    },

    fecharDialog() {
      this.dialog = false;
      this.usuarioAtual = this.usuarioVazio();
      this.$refs.form.resetValidation();
    }
  }
};
</script>

<style scoped>
.v-chip-group {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
</style>
